import React, {useEffect, useMemo, useState} from "react";
import {v4} from "uuid";

import styles from "./KnowledgeRecordsTab.module.css";
import {Button, ButtonState} from "../../elements/button/Button";
import {Section} from "../../elements/section/Section";
import {KnowledgeScope} from "../../../api/generated";
import {
    BuiltInKnowledgeType,
    KnowledgeRecordAttributeUi,
    KnowledgeRecordUi,
    UpdateKnowledgeRecordRequest
} from "../assistant/franchiseTypes";
import {SelectDropdown, SelectOption} from "../../elements/select/SelectDropdown";
import {CustomTextAreaField} from "../../elements/customTextAreaField/CustomTextAreaField";
import {useAppDispatch, useAppSelector} from "../../../app/hooks";
import {openPopupModal, PopupModalName} from "../../elements/popupModalProvider/popupModalSlice";
import {getKnowledgeTypeTitle} from "../../../utils/enumValueTitleUtil";
import {showErrorToastNotification} from "../../elements/toastNotification/toastNotificationSlice";
import {useRequestErrorToast} from "../../../hooks/useRequestErrorToast";
import {useClientPagination} from "../../../hooks/useClientPagination";
import {TablePagination} from "../../elements/pagination/TablePagination";
import {
    fetchKnowledgeAttributes,
    fetchKnowledgeTypes,
    knowledgeAttributesSelector,
    knowledgeAttributesStatusSelector,
    knowledgeTypesSelector,
    knowledgeTypesStatusSelector
} from "./knowledgeDefinitionsSlice";
import {KNOWLEDGE_RECORD_CONTENT_MAX_LENGTH, KnowledgeTypeDefinitionUi} from "./knowledgeDefinitionsApi";
import {
    getAttributeName,
    hasBlankAttributeValue,
    KnowledgeRecordAttributesField
} from "./KnowledgeRecordAttributesField";

type DraftKnowledgeRecord = KnowledgeRecordUi & {
    draftId: string;
    attributes: KnowledgeRecordAttributeUi[];
}

interface Props {
    records?: KnowledgeRecordUi[];
    scope: KnowledgeScope;
    onSave: (records: UpdateKnowledgeRecordRequest[]) => Promise<void> | void;
}

const PREVIEW_LENGTH = 360;
export const KNOWLEDGE_RECORDS_ON_PAGE = 10;

const getPreviewContent = (content: string): string =>
    content.length > PREVIEW_LENGTH ? `${content.substring(0, PREVIEW_LENGTH).trim()}...` : content;

/**
 * Built-in and custom types as the platform lists them. The record's own type is kept as an option
 * even when it is missing from the list (not loaded yet, or deleted meanwhile), so editing a record
 * never silently switches its type.
 */
const getKnowledgeTypeOptions = (types: KnowledgeTypeDefinitionUi[], currentType: string): SelectOption<string>[] => {
    const options = types.map(type => ({label: type.name, value: type.key}));
    if (!options.some(option => option.value === currentType)) {
        options.push({label: getKnowledgeTypeTitle(currentType, types), value: currentType});
    }
    return options;
};

const toDraftRecord = (
    record: KnowledgeRecordUi,
    scope: KnowledgeScope
): DraftKnowledgeRecord => ({
    ...record,
    scope,
    attributes: record.attributes || [],
    draftId: record.id ? String(record.id) : v4()
});

const newDraftRecord = (
    scope: KnowledgeScope
): DraftKnowledgeRecord => ({
    draftId: v4(),
    scope,
    knowledgeType: BuiltInKnowledgeType.GENERAL_INFORMATION,
    content: "",
    attributes: []
});

const normalizeRecords = (records: DraftKnowledgeRecord[]): UpdateKnowledgeRecordRequest[] =>
    records
        .filter(record => record.content.trim())
        .map(record => ({
            id: record.id,
            knowledgeType: record.knowledgeType,
            content: record.content.trim(),
            attributes: record.attributes.map(attribute => ({key: attribute.key, value: attribute.value.trim()}))
        }));

/**
 * The records as last saved, with only the given draft replaced (or added first, when new). Other
 * records still open for editing keep their unsaved changes out of the request.
 */
const withDraft = (savedRecords: DraftKnowledgeRecord[], draft: DraftKnowledgeRecord): DraftKnowledgeRecord[] =>
    savedRecords.some(record => record.draftId === draft.draftId)
        ? savedRecords.map(record => record.draftId === draft.draftId ? draft : record)
        : [draft, ...savedRecords];

/**
 * The platform's records, except those still open for editing, which keep their drafts. New records
 * not saved yet stay first. Records gone from the platform are dropped.
 */
const keepOpenDrafts = (
    savedRecords: DraftKnowledgeRecord[],
    drafts: DraftKnowledgeRecord[],
    editingIds: string[]
): DraftKnowledgeRecord[] => {
    const openDrafts = new Map(drafts
        .filter(record => editingIds.includes(record.draftId))
        .map(record => [record.draftId, record]));
    const unsavedDrafts = drafts.filter(record => !record.id && openDrafts.has(record.draftId));
    return [...unsavedDrafts, ...savedRecords.map(record => openDrafts.get(record.draftId) ?? record)];
};

export const KnowledgeRecordsTab: React.FC<Props> = ({
                                                         records,
                                                         scope,
                                                         onSave
                                                     }) => {
    const dispatch = useAppDispatch();
    const knowledgeTypes = useAppSelector(knowledgeTypesSelector);
    const knowledgeTypesStatus = useAppSelector(knowledgeTypesStatusSelector);
    const attributeDefinitions = useAppSelector(knowledgeAttributesSelector);
    const attributeDefinitionsStatus = useAppSelector(knowledgeAttributesStatusSelector);
    const initialRecords = useMemo(
        () => (records || []).map(record => toDraftRecord(record, scope)),
        [records, scope]
    );

    const [draftRecords, setDraftRecords] = useState<DraftKnowledgeRecord[]>(initialRecords);
    const [editingIds, setEditingIds] = useState<string[]>([]);
    const [saving, setSaving] = useState(false);
    // Saving sends the owner's whole list, so the records are paged here rather than by the platform.
    const {pageNumber, setPageNumber, pageItems, totalItems} =
        useClientPagination(draftRecords, KNOWLEDGE_RECORDS_ON_PAGE);

    // After a save the page reloads the owner: records still open for editing stay open with their drafts.
    useEffect(() => {
        const nextRecords = keepOpenDrafts(initialRecords, draftRecords, editingIds);
        setDraftRecords(nextRecords);
        setEditingIds(current => current.filter(id => nextRecords.some(record => record.draftId === id)));
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [initialRecords]);

    useEffect(() => {
        dispatch(fetchKnowledgeTypes());
        dispatch(fetchKnowledgeAttributes());
    }, [dispatch]);

    useRequestErrorToast(knowledgeTypesStatus, "Failed to load knowledge types");
    useRequestErrorToast(attributeDefinitionsStatus, "Failed to load knowledge attributes");

    const persist = async (nextRecords: DraftKnowledgeRecord[]): Promise<boolean> => {
        setSaving(true);
        try {
            await onSave(normalizeRecords(nextRecords));
            // New values become suggestions for the next record.
            dispatch(fetchKnowledgeAttributes());
            return true;
        } catch (error: any) {
            dispatch(showErrorToastNotification({message: error?.message || "Failed to update knowledge records"}));
            return false;
        } finally {
            setSaving(false);
        }
    };

    const updateDraft = (draftId: string, update: Partial<DraftKnowledgeRecord>) => {
        setDraftRecords(current => current.map(record => record.draftId === draftId ? {...record, ...update} : record));
    };

    const addRecord = () => {
        const record = newDraftRecord(scope);
        setDraftRecords(current => [record, ...current]);
        setEditingIds(current => [record.draftId, ...current]);
        // New records go first, so the first page shows the one just added.
        setPageNumber(0);
    };

    const saveRecord = async (draftId: string) => {
        const record = draftRecords.find(item => item.draftId === draftId);
        if (!record || !record.content.trim() || hasBlankAttributeValue(record.attributes)) {
            return;
        }

        if (await persist(withDraft(initialRecords, record))) {
            setEditingIds(current => current.filter(id => id !== draftId));
        }
    };

    const cancelEdit = (draftId: string) => {
        const initialRecord = initialRecords.find(record => record.draftId === draftId);
        if (!initialRecord) {
            setDraftRecords(current => current.filter(record => record.draftId !== draftId));
        } else {
            setDraftRecords(current => current.map(record => record.draftId === draftId ? initialRecord : record));
        }
        setEditingIds(current => current.filter(id => id !== draftId));
    };

    const deleteRecord = async (draftId: string) => {
        await persist(initialRecords.filter(record => record.draftId !== draftId));
    };

    const requestDeleteRecord = (draftId: string) => {
        dispatch(openPopupModal({
            name: PopupModalName.DELETE_KNOWLEDGE_RECORD,
            popupModalProps: {
                onConfirm: () => deleteRecord(draftId)
            }
        }));
    };

    const openContentPreview = (record: DraftKnowledgeRecord) => {
        dispatch(openPopupModal({
            name: PopupModalName.VIEW_MORE,
            popupModalProps: {
                title: getKnowledgeTypeTitle(record.knowledgeType, knowledgeTypes),
                value: record.content
            }
        }));
    };

    return <Section
        title={"Knowledge"}
        fillHeight={true}
        actions={<Button
            title={"Add new"}
            iconSrc={"/plusIcon.svg"}
            state={saving ? ButtonState.DISABLED : ButtonState.PRIMARY}
            handleClick={addRecord}
        />}
    >
        <div className={styles.wrapper}>
            {!draftRecords.length && <div className={styles.emptyState}>No knowledge records configured yet</div>}
            {draftRecords.length > 0 && <div className={styles.records}>
                {pageItems.map(record => {
                    const isEditing = editingIds.includes(record.draftId);
                    const canSave = !!record.content.trim() && !hasBlankAttributeValue(record.attributes) && !saving;
                    return <div className={`${styles.record} ${isEditing ? styles.editingRecord : ""}`} key={record.draftId}>
                        {isEditing ? <>
                            <div className={styles.recordFields}>
                                <div className={styles.selectField}>
                                    <SelectDropdown<string>
                                        title={"Type"}
                                        value={record.knowledgeType}
                                        handleChange={knowledgeType => updateDraft(record.draftId, {knowledgeType})}
                                        options={getKnowledgeTypeOptions(knowledgeTypes, record.knowledgeType)}
                                        selectFirstOptionByDefault={false}
                                    />
                                </div>
                                <CustomTextAreaField
                                    isRequired={true}
                                    title={"Content"}
                                    value={record.content}
                                    maxLength={KNOWLEDGE_RECORD_CONTENT_MAX_LENGTH}
                                    setValue={content => updateDraft(record.draftId, {content})}
                                />
                                <KnowledgeRecordAttributesField
                                    attributes={record.attributes}
                                    definitions={attributeDefinitions}
                                    onChange={attributes => updateDraft(record.draftId, {attributes})}
                                />
                            </div>
                            <div className={styles.actions}>
                                <Button title={"Save"} state={canSave ? ButtonState.PRIMARY : ButtonState.DISABLED} handleClick={() => saveRecord(record.draftId)}/>
                                <Button title={"Cancel"} state={ButtonState.SECONDARY} handleClick={() => cancelEdit(record.draftId)}/>
                            </div>
                        </> : <>
                            <div className={styles.recordHeader}>
                                <div className={styles.pills}>
                                    <span className={styles.typePill}>{getKnowledgeTypeTitle(record.knowledgeType, knowledgeTypes)}</span>
                                    {record.attributes.map(attribute =>
                                        <span className={styles.attributePill} key={attribute.key}>
                                            {getAttributeName(attribute, attributeDefinitions)}: {attribute.value}
                                        </span>
                                    )}
                                </div>
                                <div className={styles.headerActions}>
                                    <Button title={"Edit"} state={ButtonState.SECONDARY} handleClick={() => setEditingIds(current => [record.draftId, ...current])}/>
                                    <Button title={"Delete"} state={saving ? ButtonState.DISABLED : ButtonState.SECONDARY} handleClick={() => requestDeleteRecord(record.draftId)}/>
                                </div>
                            </div>
                            <div className={styles.recordView}>
                                <p className={styles.content}>{getPreviewContent(record.content)}</p>
                                {record.content.length > PREVIEW_LENGTH &&
                                    <button
                                        type={"button"}
                                        className={styles.viewFullButton}
                                        onClick={() => openContentPreview(record)}
                                    >
                                        View full
                                    </button>
                                }
                            </div>
                        </>}
                    </div>
                })}
            </div>}
            {totalItems > 0 && <TablePagination
                pageNumber={pageNumber}
                itemsPerPage={KNOWLEDGE_RECORDS_ON_PAGE}
                totalItems={totalItems}
                onPageChange={setPageNumber}
            />}
        </div>
    </Section>
}
